import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  clearAllAuthStorage,
  getAccessToken,
  getAuthMode,
  getStoredUser,
  setAuthMode,
  setAuthSessionItem,
  setStoredUser,
  type StoredUser,
} from '@/lib/auth-storage'
import {
  clearParkingOsSession,
  setParkingOsSession,
  type ParkingOsSession,
} from '@/lib/parking-session'
import { extractKioskSession, fetchUserInfo, loginApi, logoutKiosk, normalizeLoginUser } from '@/api'
import { getApiData, getApiErrorMessage, isApiSuccess } from '@/lib/api-response'
import type { KioskLoginResult } from '@/types/auth'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<StoredUser | null>(getStoredUser())
  const loading = ref(false)
  const initialized = ref(false)

  const isAuthenticated = computed(() => !!getAccessToken())
  const displayName = computed(() => user.value?.name || user.value?.username || '')

  function hydrateFromStorage() {
    user.value = getStoredUser()
    initialized.value = true
  }

  async function login(username: string, password: string) {
    loading.value = true
    try {
      const payload = await loginApi(username, password)
      if (!isApiSuccess(payload)) {
        return { success: false, message: getApiErrorMessage(payload, '登录失败') }
      }

      const data = getApiData<KioskLoginResult>(payload)
      const session = extractKioskSession(data)
      if (!session.token) {
        return { success: false, message: '登录成功但未返回 token' }
      }
      if (!session.parkingId) {
        return { success: false, message: '当前账号没有绑定车场，无法进入云岗亭' }
      }

      setAuthSessionItem('accessToken', session.token)
      setAuthMode('platform')
      const nextUser = normalizeLoginUser(data || {})
      setStoredUser(nextUser)
      user.value = nextUser
      setParkingOsSession({
        parkingId: session.parkingId,
        parkName: session.parkName,
        token: session.token,
      })

      return { success: true }
    } catch (error) {
      return { success: false, message: error instanceof Error ? error.message : '登录失败' }
    } finally {
      loading.value = false
    }
  }

  async function refreshUser() {
    try {
      const payload = await fetchUserInfo()
      if (!isApiSuccess(payload)) return
      const data = getApiData<KioskLoginResult>(payload)
      if (!data) return
      const nextUser = normalizeLoginUser(data)
      setStoredUser(nextUser)
      user.value = nextUser
      const session = extractKioskSession(data)
      if (session.token && session.parkingId) {
        setParkingOsSession({
          parkingId: session.parkingId,
          parkName: session.parkName,
          token: session.token,
        })
      }
    } catch {
      // ignore
    }
  }

  function enterPark(session: ParkingOsSession) {
    setParkingOsSession(session)
    setAuthSessionItem('accessToken', session.token)
    if (!getAuthMode()) setAuthMode('platform')
  }

  async function logout() {
    try {
      await logoutKiosk()
    } catch {
      // ignore
    }
    clearAllAuthStorage()
    clearParkingOsSession()
    user.value = null
  }

  return {
    user,
    loading,
    initialized,
    isAuthenticated,
    displayName,
    hydrateFromStorage,
    login,
    refreshUser,
    enterPark,
    logout,
  }
})
